// THE SCOPE THE TRACKING VIEWER DRAWS UNDER, resolved BEFORE the first
// tile so the loader never has to un-build what it has already built.
//
// Why before rather than after (Paul + Mark, 2026-09-25): the row filter
// drops instances where they are BORN, so a tile that landed before the
// scope arrived stays built. Re-normalising it later would mean tearing
// down live instances mid-stream against the single-active-instance
// invariant - the octree routes a straddling instance into every tile it
// touches and only the active slot may render. That is the part of the
// system most likely to produce a wrong-but-plausible picture, so we
// pay one small call up front instead.
//
// AND IT IS BOUNDED, because holding a stream on a network call is how
// you get a BLANK VIEWER - the worst outcome this feature can produce
// and indistinguishable from a broken model. If the answer has not
// arrived inside the budget, drawing starts UNFILTERED and the page says
// the scope could not be applied.
//
// ONE DEGRADATION, FOUR CAUSES, ONE SENTENCE: refused over the limit,
// refused on a timeout, an answer for a different commit, or a transport
// failure. Never silently drawing a model the owner believes he pruned,
// and never drawing nothing.
import { useEffect, useRef, useState } from "react";
import { getExcludedUids } from "api/tracking/trackingElementsApi";
import {
  scopeFromResponse, setTrackingScope, forgetTrackingScope, SCOPE_CANNOT_APPLY,
  appliedScopeGeneration, onScopeApplied,
} from "webgl/js/loader/streaming/scopeFilter";

// A GUESS UNTIL IT IS MEASURED, and said so out loud. Mark's route
// budgets 2 s and fails fast, so in the normal failing case his answer
// arrives before this fires - which is the right shape for a timeout:
// present, tested, and normally irrelevant.
export const SCOPE_BUDGET_MS = 3000;

export function useTrackingScope(projectId, drawingCommitId, { budgetMs = SCOPE_BUDGET_MS, nonce = 0 } = {}) {
  // `ready` gates the stream. It is never left false: every path sets it.
  // `forNonce` is which ASK this answer belongs to, and it is what lets
  // the gate below be derived rather than set. See the note above the
  // return for why setting it from the effect does not work.
  const [state, setState] = useState({ ready: false, note: null, reason: null, forNonce: null });
  const seq = useRef(0);

  useEffect(() => {
    // projectId is `routeProjectId || rememberedId || ""` and CAN BE
    // EMPTY on the first render, which would fetch /projects//... and
    // return a 404 we would then have to tell apart from a refusal.
    //
    // READY IS TRUE HERE, NOT FALSE (Mark, 2026-09-25). There is no
    // scope to apply and nothing to say, so there is nothing to wait
    // for. Holding instead armed no timer and nothing ever settled it:
    // ModelTracking has no early return for a missing project - it
    // renders the stage regardless - so a person reaching the page with
    // no route id AND no remembered project would have seen "Reading
    // the tracking scope..." for ever, for something that was never
    // going to be read. It self-heals in the normal case, because the
    // id arrives a render later, which is exactly why it would not have
    // shown up in testing.
    //
    // A MESSAGE THAT IS FALSE IS WORSE THAN NO MESSAGE - my own argument
    // for holding with a sentence rather than holding silently, one
    // branch along from where I made it.
    if (!projectId) { setState({ ready: true, note: null, reason: null, forNonce: nonce }); return undefined; }
    const mine = (seq.current += 1);
    let settled = false;
    const settle = (next) => {
      if (settled || seq.current !== mine) return;
      settled = true;
      setState({ ready: true, forNonce: nonce, ...next });
    };

    forgetTrackingScope();          // a previous project's scope must not survive
    const timer = setTimeout(() => {
      // Too slow: draw everything, and SAY so.
      setTrackingScope(null, nonce);
      settle({ note: SCOPE_CANNOT_APPLY, reason: "slow" });
    }, budgetMs);

    getExcludedUids(projectId)
      .then((payload) => {
        const { excluded, note, reason } = scopeFromResponse(payload, drawingCommitId);
        setTrackingScope(excluded, nonce);
        settle({ note: note || null, reason: reason || null });
      })
      .catch(() => {
        // A NON-200 IS A REAL FAILURE, not "no exclusions": the route
        // answers 200 even when it refuses.
        setTrackingScope(null, nonce);
        settle({ note: SCOPE_CANNOT_APPLY, reason: "unreachable" });
      })
      .then(() => clearTimeout(timer));

    return () => { clearTimeout(timer); };
    // nonce: Mark's Apply button bumps it to re-resolve the scope and
    // rebuild the stage. It is the FIRST dep here that changes without
    // the page already remounting the stage for another reason, which
    // is why the derived gate below had to exist before it was added.
  }, [projectId, drawingCommitId, budgetMs, nonce]);

  // WHAT THE VIEWER SAYS IT DID, which is not what this hook committed.
  //
  // HAL, 2026-09-25: a button whose third state comes from the page is
  // reporting the page's BELIEF about the viewer. If the stage can
  // acknowledge, "applied" stops being an assumption - and a control that
  // reports success for something it cannot observe is the class we spent
  // the night before removing.
  //
  // So this is a subscription, not a derivation: scopeFilter records the
  // generation at the moment its admission check is first CONSULTED, which
  // only the loader can cause. Declared BEFORE the teardown effect below
  // so React runs this cleanup first and forgetTrackingScope cannot call
  // setState on an unmounting component.
  const [appliedNonce, setAppliedNonce] = useState(() => appliedScopeGeneration());
  useEffect(() => onScopeApplied((g) => setAppliedNonce(g)), []);

  useEffect(() => () => forgetTrackingScope(), []);

  // THE GATE IS DERIVED, NOT SET - and that is the whole reason a nonce
  // can work here.
  //
  // The obvious shape is to setState({ ready: false }) at the top of the
  // effect when the nonce changes. It is wrong, and quietly: an effect
  // runs AFTER the commit that rendered the new nonce, so for one commit
  // `ready` is still true from the PREVIOUS answer. The stage mounts
  // under the OLD committed scope and starts streaming, and then
  // setTrackingScope swaps the set MID-STREAM. Instances already born
  // keep the old verdict, and against the single-active-instance
  // invariant that is a wrong-but-plausible picture, not an error -
  // the failure mode this whole fetch-before-first-tile design exists
  // to avoid.
  //
  // Comparing the nonce this answer was RESOLVED FOR against the nonce
  // being asked for closes the gate in the SAME render as the bump: no
  // extra render, and no ordering question. By the time the effect body
  // above runs, the stage is already unmounted, so forgetTrackingScope
  // cannot reach a live registry.
  // NO PROJECT SHORT-CIRCUITS THE GATE, and a test caught that it did
  // not. With no projectId there is nothing to resolve, so the nonce is
  // meaningless - but the derived comparison still saw forNonce (0)
  // against a bumped nonce (7) and closed. One render of "Reading the
  // tracking scope..." for a page that will never read one: the same
  // false message Mark found on the first render, reintroduced one
  // branch along. It self-heals a render later, which is exactly why it
  // needed a test rather than a look.
  const ready = !projectId || (state.ready && state.forNonce === nonce);
  return {
    ready,
    // A note from a SUPERSEDED ask must not sit over the new one.
    note: ready ? state.note : null,
    reason: ready ? state.reason : null,
    // The generation the LOADER has filtered under, reported by the
    // loader. Mark's third state is savedNonce !== appliedNonce.
    //
    // NOT gated on `ready`, and not taken from state.forNonce: committing
    // a scope is not applying it. Between a resolve and the first tile
    // being normalised this still reads the PREVIOUS generation, which is
    // exactly the window the middle state exists to describe.
    appliedNonce,
  };
}
